import * as L from "leaflet";
import { getStationById } from "../domain/storage/stationsStore";
import { DataSampleType, Station, StationStatus } from "../domain/types";
import { getLatestSample } from "./data/latestData";
import { circleIcon, getAnimationContext } from "./markers/circleIcon";
import { markerToStation } from "./markers/createMarker";
import { getColorByValueAndType } from "../domain/utils/colors";
import { getStatusManager } from "./state/statusLayers";

const offset: Record<StationStatus, number[]> = {
	fresh: [0, 0],
	stale: [20, 0],
	offline: [0, 20]
}
function getDefaultClusterIcon(cluster: L.MarkerCluster, color: string | null = null, status: StationStatus) {
	let childCount = cluster.getChildCount();
	let size = new L.Point(40, 40);
	let c = ' marker-cluster-';
	if (childCount < 10) {
		c += 'small';
	} else if (childCount < 100) {
		c += 'medium';
	} else {
		c += 'large';
	}
	let style = "";
	if (color) {
		c = '';
		if (status === 'offline') {
			color = 'rgba(189,189,189,0.5)';
		}
		if (status) {
			style = 'font-size:130%';
			//style = `stroke-dasharray="4 3" stroke="${color}" stroke-width="2" fill="none"`;
			const ringStroke = status == "stale" ? `stroke-dasharray="4 3" stroke="white" stroke-width="2" fill="none"` : `fill="none"`;
			const overlayOffline = status === 'offline'
				? `<line x1="8" y1="8" x2="${size.x - 8}" y2="${size.y - 8}" stroke="#BDBDBD" stroke-width="2"/>
       <line x1="${size.x - 8}" y1="8" x2="8" y2="${size.y - 8}" stroke="#BDBDBD" stroke-width="2"/>`
				: '';
			//`<circle cx="${size.x / 2}" cy="${size.y / 2}" r="${(size.x / 2) - 4}" ${ringStroke}/>`;
			const svg = `<svg class="ring" width="${size.x}" height="${size.y}" viewBox="0 0 ${size.x} ${size.y}">
			<circle cx="${size.x / 2}" cy="${size.y / 2}" r="${(size.x / 2)}" fill="${color}"/>
			<circle cx="${size.x / 2}" cy="${size.y / 2}" r="${(size.x / 2) - 4}" ${ringStroke}/>
			${overlayOffline}
			<text x="50%" y="50%" text-anchor="middle" dominant-baseline="middle" dY="0.1em"
    font-family="Helvetica Neue, Arial, Helvetica, sans-serif" font-size="130%" fill="#fff" stroke="rgba(0,0,0,0.8)" stroke-width="1" paint-order="stroke"> ${childCount}</text>
        </svg>`
			//return new L.DivIcon({ html: `<div style="${style}">${svg}</div>`, className: 'marker-cluster' + c, iconSize: size });
			//return new L.DivIcon({ html: `<div style="${style}"><div class="circle-icon">${svg}</div><span style="position: absolute; top: 10px; left: 25px;z-index:200;font-size:130%;">${childCount}</span></div>`, className: 'marker-cluster', iconSize: size });
			return new L.DivIcon({
				html: `<div class="marker-cluster stale" style="width:${size.x}px;height:${size.y}px;${style}">
                 ${svg}
                <span class="count"></span>
               </div>`,
				className: 'marker-cluster',
				iconSize: size
			})
		}
		style = `background-color: ${color};font-size:130%;`;
	}

	return new L.DivIcon({ html: `<div style="${style}"><span>` + childCount + '</span></div>', className: 'marker-cluster' + c, iconSize: size, iconAnchor: [20-offset[status][0], 20-offset[status][1]] });
}

function getHighestValue(children: L.Marker<any>[]) {
	let highestValue = Number.MIN_VALUE;
	let highestSampleType = DataSampleType.NONE;
	let selectedStationId = "";
	for (const child of children) {
		const stationId = markerToStation.get(child);
		const station = getStationById(stationId);
		if (station) {
			const animation = getAnimationContext(station);
			const value = getLatestSample(station, animation.type);
			const currentValue = (value && value.Valid ? value.Value : Number.MIN_VALUE);
			if (currentValue > highestValue) {
				highestValue = currentValue;
				highestSampleType = animation.type;
				selectedStationId = station.Id;
			}
		}
	}
	return { value: highestValue, color: getColorByValueAndType(highestValue, highestSampleType), status: getStatusManager().getStatus(selectedStationId) ?? "offline" };
}

function getHighestValueCommon(children: L.Marker<any>[]) {
	let commonType: DataSampleType | null = null;
	let highestMarkerStation: { station: Station, value: number } | null = null;
	for (const child of children) {
		const stationId = markerToStation.get(child);
		const station = getStationById(stationId);
		if (station) {
			const animation = getAnimationContext(station);
			if (commonType) {
				if (commonType === animation.type) {
				} else {
					commonType = null;
					highestMarkerStation = null;
					break;
				}
			} else {
				commonType = animation.type;
			}
			const value = getLatestSample(station, animation.type);
			if (commonType) {
				if (!highestMarkerStation) {
					highestMarkerStation = { station: station, value: (value && value.Valid ? value.Value : Number.MIN_VALUE) };
				}
				if (value && (value.Valid && value.Value >= highestMarkerStation.value)) {
					highestMarkerStation.value = value.Value;
				}
			}
		}
	}
	return { value: highestMarkerStation?.value ?? 0, color: getColorByValueAndType(highestMarkerStation?.value ?? 0, commonType ?? DataSampleType.NONE) };
}
export function createClusterGroup(): L.MarkerClusterGroup {
	return L.markerClusterGroup({
		disableClusteringAtZoom: 12,
		zoomToBoundsOnClick: true,
		spiderfyOnMaxZoom: true,
		animateAddingMarkers: false,
		chunkedLoading: true,
		chunkInterval: 16,
		chunkDelay: 0,
		removeOutsideVisibleBounds: false,
		spiderfyDistanceMultiplier: 8,
		showCoverageOnHover: true,
		maxClusterRadius: 50,
		iconCreateFunction: (cluster) => {
			const children = cluster.getAllChildMarkers();
			let highestValue = getHighestValue(children);
			//if (highestMarkerStation && highestMarkerStation.value !== Number.MIN_VALUE) {
			//	return circleIcon(highestMarkerStation.station, false,41,100);
			//}
			return getDefaultClusterIcon(cluster, highestValue.color, highestValue.status);
		}
	});
}

export function wireGlobalMapEvents(map: L.Map, cluster: L.MarkerClusterGroup) {
	// Clicking empty map closes any open badges
	map.on("click", (e: any) => {
		const t = e.originalEvent?.target as HTMLElement | null;
		if (!t?.closest(".leaflet-marker-icon")) {
			document.querySelectorAll(".leaflet-marker-icon.badges-open")
				.forEach(el => el.classList.remove("badges-open"));
		}
	});

	// Popup open = disable hover globally, force badges open
	map.on("popupopen", (e: any) => {
		map.getContainer().classList.add("no-hover-badges");
		const src = e.popup?.getSource?.() ?? e.popup?._source;
		if (src && src instanceof L.Marker) src.getElement()?.classList.add("badges-open");
	});

	map.on("popupclose", (e: any) => {
		map.getContainer().classList.remove("no-hover-badges");
		const src = e.popup?.getSource?.() ?? e.popup?._source;
		if (src && src instanceof L.Marker) src.getElement()?.classList.remove("badges-open");
	});
}
